import React, { useEffect, useMemo, useRef, useState } from "react";
import { Workspace } from "../types";

/** Case-insensitive check for personal ("My workspace") workspaces. */
export function isPersonalWorkspace(ws: Workspace): boolean {
  return (ws.type ?? "").toLowerCase().startsWith("personal");
}

const MAX_RESULTS = 100;

interface WorkspaceComboboxProps {
  workspaces: Workspace[];
  value: string; // selected workspaceId ("" = all)
  onChange: (workspaceId: string) => void;
  placeholder?: string;
  loading?: boolean;
}

/**
 * Searchable workspace picker — type to filter by name instead of scrolling
 * a native <select> with thousands of entries.
 */
export default function WorkspaceCombobox({
  workspaces,
  value,
  onChange,
  placeholder = "All workspaces — type to search…",
  loading = false,
}: WorkspaceComboboxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => workspaces.find((ws) => ws.workspaceId === value) ?? null,
    [workspaces, value]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q
      ? workspaces.filter((ws) => (ws.name ?? "").toLowerCase().includes(q))
      : workspaces;
    return pool.slice(0, MAX_RESULTS);
  }, [workspaces, query]);

  const totalMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? workspaces.filter((ws) => (ws.name ?? "").toLowerCase().includes(q)).length
      : workspaces.length;
  }, [workspaces, query]);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => setHighlighted(0), [query]);

  const select = (workspaceId: string) => {
    onChange(workspaceId);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (matches[highlighted]) select(matches[highlighted].workspaceId);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className="relative w-72">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="workspace-combobox-listbox"
          aria-label="Filter by workspace"
          value={open ? query : selected?.name ?? ""}
          placeholder={loading ? "Loading workspaces…" : selected && !open ? selected.name : placeholder}
          disabled={loading}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={handleKeyDown}
          className="w-full border border-gray-300 rounded-lg pl-8 pr-8 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
        {/* Search icon / loading spinner */}
        {loading ? (
          <svg
            className="w-4 h-4 text-gray-400 animate-spin absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
            fill="none" viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        ) : (
          <svg
            className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
          </svg>
        )}
        {/* Clear button */}
        {(selected || query) && (
          <button
            type="button"
            aria-label="Clear workspace filter"
            onClick={() => { select(""); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {open && (
        <ul
          id="workspace-combobox-listbox"
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1 text-sm"
        >
          <li>
            <button
              type="button"
              onClick={() => select("")}
              className={`w-full text-left px-3 py-1.5 hover:bg-blue-50 ${!value ? "text-blue-700 font-medium" : "text-gray-600"}`}
            >
              All workspaces
            </button>
          </li>
          {matches.map((ws, i) => (
            <li key={ws.workspaceId}>
              <button
                type="button"
                onClick={() => select(ws.workspaceId)}
                onMouseEnter={() => setHighlighted(i)}
                className={`w-full text-left px-3 py-1.5 flex items-center justify-between gap-2 ${
                  i === highlighted ? "bg-blue-50" : ""
                } ${ws.workspaceId === value ? "text-blue-700 font-medium" : "text-gray-700"}`}
              >
                <span className="truncate">{ws.name}</span>
                {isPersonalWorkspace(ws) && (
                  <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    personal
                  </span>
                )}
              </button>
            </li>
          ))}
          {matches.length === 0 && (
            <li className="px-3 py-2 text-gray-400">No workspaces match “{query}”</li>
          )}
          {totalMatches > matches.length && (
            <li className="px-3 py-1.5 text-xs text-gray-400 border-t border-gray-100">
              Showing first {matches.length} of {totalMatches} — keep typing to narrow down
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
